import { differenceInCalendarDays } from 'date-fns';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { useApplicationConfig } from '@bonusly/application';
import { Box, ClockOutline, HStack, Link, Text } from '@bonusly/design-system';
import { joinPath } from '@bonusly/http';
import { useCurrentCompany, useCurrentUser } from '@bonusly/who-am-i';

const OFFICE_HOURS_URL = 'https://calendly.com/bonusly-expert-office-hours';

interface TrialCardProps {
	isExpanded: boolean;
}

export const TrialCard: FC<TrialCardProps> = ({ isExpanded }) => {
	const keyPrefix = 'trial_card';
	const { t } = useTranslation('navigation', { keyPrefix });
	const user = useCurrentUser();
	const company = useCurrentCompany();
	const { track } = useAnalytics();
	const origin = useApplicationConfig('recognizeOrigin');

	const hasTrialEndDate = !!company.trial_end_date && company.status === 'in_trial';

	if (!isExpanded || !user?.admin || !hasTrialEndDate || company.is_superadmin_initialized) return null;

	const count = differenceInCalendarDays(new Date(company.trial_end_date), new Date());

	return (
		<Box bgColor="bg.surface" borderRadius="md" my={2} p={3}>
			<Text color="text.default" fontSize="sm" fontWeight="semibold">
				{t('title', { plan: company.product_plan_name })}
			</Text>

			<HStack mt={1} spacing={1}>
				<ClockOutline color="text.default" flexShrink={0} size="xs" />
				<Text color="text.default" fontSize="sm">
					{t('days', { count })}
				</Text>
			</HStack>

			<Link
				color="text.interactive"
				display="block"
				fontSize="sm"
				fontWeight="medium"
				href={joinPath(origin, '/company/billing')}
				mt={2}
				onClick={() => track(AnalyticsEvent.OnboardingTrialBanner.ManagePlanClicked)}
				_hover={{ textDecor: 'none' }}
			>
				{t('manage_plan')}
			</Link>

			<Box mt={2} pt={2} borderTop="1px solid" borderTopColor="border.default">
				<Text color="text.softer" fontSize="sm">
					{t('need_help')}{' '}
					<Link href={OFFICE_HOURS_URL} target="_blank" _hover={{ textDecor: 'none' }}>
						{t('talk_to_expert')}
					</Link>
				</Text>
			</Box>
		</Box>
	);
};
